// react
import { useContext, useMemo, useState } from 'react';

// types
import { Loan, Pagination } from '../../../../@types';

// i18next
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardLoan } from '../../../cards/CardLoan/CardLoan';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// fields
import { SelectCurrency, SelectCurrencyProps } from '../../../fields/SelectCurrency/SelectCurrency';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { PaymentsOutlined as IconPayments } from '@mui/icons-material';

// utils
import { getOptionsFinanciers } from '../../../../utils/options';

// configs
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TableFinancingSourcesProps {
	currency?: string;
	hasFilters?: boolean;
	hasSearch?: boolean;
	isLoading?: boolean;
	loans: Loan[];
	pagination?: Pagination;
	SelectCurrencyProps?: SelectCurrencyProps;
}

export const TableFinancingSources = ({
	currency: currencyProp,
	hasFilters = true,
	hasSearch = true,
	isLoading,
	loans,
	pagination,
	SelectCurrencyProps,
}: TableFinancingSourcesProps) => {
	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramFinancier = searchParams.get('financier');

	// options
	const optionsFinanciers = getOptionsFinanciers();

	// defaults
	const defaultFiltersValues = {
		financier: optionsFinanciers.map((option) => option.value),
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		financier: paramFinancier ? paramFinancier.split(',') : defaultFiltersValues.financier,
	});

	// vars
	const idFormFilters = `form-financing-sources-filters`;

	const headers: (TableHeaderProps | undefined)[] = [
		{
			GridProps: {
				lg: 2,
			},
			label: t('id'),
			value: 'id',
		},
		// Only show name column when Financial Traceability is disabled
		...(featureFlags['FINANCIAL_TRACEABILITY']
			? []
			: [
					{
						GridProps: {
							lg: 2,
						},
						label: t('name'),
						value: 'name',
					},
				]),
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('closing'),
			value: 'dateClosing',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 1,
			},
			label: t('undisbursed'),
			value: 'undisbursed',
		},
		featureFlags['UTILIZATION']
			? {
					GridProps: {
						lg: 1,
					},
					label: t('fundUtilization'),
					tooltip: {
						title: t('tooltip.utilization'),
					},
					value: 'utilization',
				}
			: undefined,
		{
			GridProps: {
				lg: 1,
			},
			label: t('transactionsEvidenced'),
			tooltip: {
				title: t('tooltip.transactionsEvidenced'),
			},
			value: 'traceability',
		},
		// Add financial traceability column conditionally based on feature flag
		...(featureFlags['FINANCIAL_TRACEABILITY']
			? [
					{
						GridProps: {
							lg: 1,
						},
						label: t('financialValueEvidenced'),
						tooltip: {
							title: t('tooltip.financialValueEvidenced'),
						},
						value: 'traceabilityFinancial',
					},
				]
			: []),
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('status'),
			value: 'status',
		},
	].filter(Boolean);

	const currency = currencyProp || (SelectCurrencyProps?.value as string);

	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [
		{
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-financier`,
			label: t('financier'),
			multiple: true,
			name: 'financier',
			onChange: handleChangeFilterValue,
			options: optionsFinanciers,
			type: 'SELECT',
			value: filtersValues.financier,
		},
	];

	const content = useMemo(() => {
		if (isLoading || !project || !loans.length) {
			const actions: EmptyProps['actions'] = [];

			if (userProjectPermissions?.loan.create) {
				actions.push({
					children: t('addFinancingSource'),
					onClick: () => {
						searchParams.set('dialog', 'addFinancingSource');
						setSearchParams(searchParams);
					},
					variant: 'contained',
				});
			}

			return (
				<CardEmpty
					empty={{
						actions,
						content: { children: t('empty.loans.content') },
						IconCircleProps: { icon: <IconPayments /> },
						title: { children: t('empty.loans.title') },
					}}
					isLoading={isLoading}
				/>
			);
		}

		return (
			<Grid container spacing={2}>
				{loans.map((loan, i) => {
					return (
						<Grid item key={`loan-${loan.code}-${i}`} xs={12}>
							<CardLoan currency={currency} loan={loan} project={project} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [
		currency,
		isLoading,
		loans,
		project,
		searchParams,
		setSearchParams,
		t,
		userProjectPermissions,
	]);

	return (
		<Grid container spacing={2}>
			{(hasSearch || hasFilters) && (
				<Grid item xs={12}>
					<Grid container justifyContent={hasSearch ? 'flex-start' : 'flex-end'} spacing={2}>
						{hasSearch && (
							<Grid item xs={12} md={true}>
								<FormSearch InputProps={{ placeholder: t('searchById') }} />
							</Grid>
						)}
						{SelectCurrencyProps && (
							<Grid item xs={hasFilters ? 6 : 12} md={2} xl={1.5}>
								<SelectCurrency {...SelectCurrencyProps} />
							</Grid>
						)}
						{hasFilters && (
							<Grid item xs={SelectCurrencyProps ? 6 : 12} md="auto">
								<BtnFilters
									filters={filters}
									onReset={() => {
										setFiltersValues(defaultFiltersValues);
									}}
								/>
							</Grid>
						)}
					</Grid>
				</Grid>
			)}
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers as TableHeaderProps[]} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			{pagination && (
				<Grid item xs={12}>
					<TableFooter numShowing={loans.length} pagination={pagination} />
				</Grid>
			)}
		</Grid>
	);
};
